import { Button, Image } from '@storybook';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
	BusinessPaymentCardState,
	SubscriptionPriceState,
	loginState,
} from 'global-stores';
import { useFullStoryTrackEvent } from 'hooks';
import { isBillingAddressComplete } from 'utils';
import { useBillingTransaction } from 'views/settings';
import {
	ChooseIdentityData,
	IndetityFlowNavigate,
} from 'views/user-identity-flow/constants';
import {
	ChooseUserIdentity,
	IdentityFlowCompletion,
	SubscriptionIdentityNaviagtion,
} from 'views/user-identity-flow/store';

export const SuccessPage = ({ handleClose }: { handleClose: () => void }) => {
	const activeIdx = useRecoilValue(ChooseUserIdentity);
	// __simplici_success_variant__: identity verification also ends here and takes
	// no payment, so the subscription copy, the `subscribed` analytics event, the
	// billing-transaction fetch and the "Add billing info" button must not fire for
	// it — all four would be untrue.
	const completion = useRecoilValue(IdentityFlowCompletion);
	const isVerification = completion === 'verification';
	const businessPaymentCardState = useRecoilValue(BusinessPaymentCardState);
	const setNavigate = useSetRecoilState(SubscriptionIdentityNaviagtion);
	const user = useRecoilValue(loginState);
	const subscriptionPrice = useRecoilValue(SubscriptionPriceState);

	const price = useMemo(() => {
		if (subscriptionPrice) {
			return (subscriptionPrice as any)[0]?.[activeIdx];
		} else return 0;
	}, [activeIdx, subscriptionPrice]);

	const { billingTransaction: getBillingTransaction } = useBillingTransaction();
	const { trackEvent } = useFullStoryTrackEvent();

	const accountTypeLabel = useMemo(
		() => ChooseIdentityData.find(el => el.type === activeIdx),
		[activeIdx]
	);

	const trackFSSession = useCallback(() => {
		const {
			userId,
			firstName,
			approveStatus = '',
			lastName,
			companyName = '',
			countryCode,
			phone,
			email,
			accountType = '',
		} = user ?? {};
		const properties = {
			user: {
				userId,
				firstName,
				lastName,
				phone: `${countryCode} ${phone}`,
				email,
			},
			createdAt: new Date(),
			subscriptionName: accountType,
			amount: price,
			businessName: companyName,
			businessApproved: approveStatus,
		};

		trackEvent('subscribed', properties);
	}, [price, trackEvent, user]);

	// __simplici_billing_address_loop__: `address2` (House/Apt./Suite) is
	// optional and the BillingForm saves without it, but the old check here
	// counted it, so a saved address with a blank `address2` still read as
	// incomplete — "Add billing info" reappeared, saved again, and returned
	// here: the update-billing loop. isBillingAddressComplete checks only the
	// required fields, the same set the form enforces.
	const hasBillingAddress = useMemo(
		() => isBillingAddressComplete(businessPaymentCardState?.billingAddress),
		[businessPaymentCardState]
	);

	const addBillingInfo = () => {
		setNavigate(IndetityFlowNavigate.BillingForm);
	};

	useEffect(() => {
		if (isVerification) return;
		trackFSSession();
		getBillingTransaction();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="SuccessPage">
			<div className="SuccessPage--icon">
				<Image fileName="success.gif" loading="lazy" />
			</div>
			<div className="SuccessPage--info">
				<h2 className="SuccessPage--info__heading">
					{isVerification ? 'Verification complete' : 'Subscribed successfully'}
				</h2>
				<p className="SuccessPage--info__desc">
					{isVerification ? (
						<>
							You&apos;ve completed every step for{' '}
							<span>{accountTypeLabel?.label}.</span> We&apos;ll review your
							details and let you know once your account is approved.
						</>
					) : (
						<>
							You&apos;ve successfully subscribed for{' '}
							<span>{accountTypeLabel?.label}.</span>
						</>
					)}
				</p>
			</div>
			{!isVerification && !hasBillingAddress ? (
				<Button
					handleClick={addBillingInfo}
					label="Add billing info"
					type="button__filled button__filled--primary button__large"
				/>
			) : (
				<Button
					handleClick={handleClose}
					label="Close"
					type="button__filled button__filled--secondary button__large"
				/>
			)}
		</div>
	);
};
